/**
 * Material List Utilities
 * Helper functions for managing material list states and change detection
 */

/**
 * Create a snapshot of line items for change detection
 * @param {Array} lineItems - Array of line items
 * @returns {Object} Snapshot object for comparison
 */
export const createLineItemsSnapshot = (lineItems) => {
  if (!lineItems || !Array.isArray(lineItems)) {
    return { items: [], hash: '' };
  }

  // Create a simplified snapshot focusing on material-relevant fields
  const snapshot = lineItems.map(item => ({
    id: item.id,
    workingHeight: item.workingHeight,
    width: item.width,
    quantity: item.quantity,
    services: {
      transportation: item.services?.transportation || false,
      assembly: item.services?.assembly || false,
      endorsement: item.services?.endorsement || false
    }
  }));

  // Create a hash for quick comparison
  const hash = JSON.stringify(snapshot);

  return {
    items: snapshot,
    hash,
    createdAt: new Date().toISOString()
  };
};

/**
 * Detect if line items have changed compared to saved snapshot
 * @param {Array} currentLineItems - Current line items
 * @param {Object} savedSnapshot - Previously saved snapshot
 * @returns {boolean} True if changes detected
 */
export const detectLineItemChanges = (currentLineItems, savedSnapshot) => {
  if (!savedSnapshot) {
    return false; // No snapshot to compare against
  }

  const currentSnapshot = createLineItemsSnapshot(currentLineItems);
  
  // Quick hash comparison first
  if (currentSnapshot.hash === savedSnapshot.hash) {
    return false;
  }

  // If hashes differ, there are changes
  return true;
};

/**
 * Get detailed changes between current line items and saved snapshot
 * @param {Array} currentLineItems - Current line items
 * @param {Object} savedSnapshot - Previously saved snapshot
 * @returns {Object} Detailed change information
 */
export const getLineItemChanges = (currentLineItems, savedSnapshot) => {
  if (!savedSnapshot) {
    return { hasChanges: false, changes: [] };
  }

  const currentSnapshot = createLineItemsSnapshot(currentLineItems);
  const changes = [];

  // Compare items
  const currentItems = currentSnapshot.items;
  const savedItems = savedSnapshot.items || [];

  // Check for added items
  currentItems.forEach(currentItem => {
    const savedItem = savedItems.find(s => s.id === currentItem.id);
    if (!savedItem) {
      changes.push({
        type: 'added',
        itemId: currentItem.id,
        description: 'New line item added'
      });
    }
  });

  // Check for removed items
  savedItems.forEach(savedItem => {
    const currentItem = currentItems.find(c => c.id === savedItem.id);
    if (!currentItem) {
      changes.push({
        type: 'removed',
        itemId: savedItem.id,
        description: 'Line item removed'
      });
    }
  });

  // Check for modified items
  currentItems.forEach(currentItem => {
    const savedItem = savedItems.find(s => s.id === currentItem.id);
    if (savedItem) {
      const itemChanges = [];
      
      if (currentItem.workingHeight !== savedItem.workingHeight) {
        itemChanges.push(`Working height: ${savedItem.workingHeight} → ${currentItem.workingHeight}`);
      }
      
      if (currentItem.width !== savedItem.width) {
        itemChanges.push(`Width: ${savedItem.width} → ${currentItem.width}`);
      }
      
      if (currentItem.quantity !== savedItem.quantity) {
        itemChanges.push(`Quantity: ${savedItem.quantity} → ${currentItem.quantity}`);
      }

      // Check services changes
      const currentServices = currentItem.services || {};
      const savedServices = savedItem.services || {};
      
      Object.keys(currentServices).forEach(serviceKey => {
        if (currentServices[serviceKey] !== savedServices[serviceKey]) {
          const status = currentServices[serviceKey] ? 'added' : 'removed';
          itemChanges.push(`${serviceKey} service ${status}`);
        }
      });

      if (itemChanges.length > 0) {
        changes.push({
          type: 'modified',
          itemId: currentItem.id,
          description: `Modified: ${itemChanges.join(', ')}`
        });
      }
    }
  });

  return {
    hasChanges: changes.length > 0,
    changes,
    changeCount: changes.length
  };
};

/**
 * Material list state constants
 */
export const MATERIAL_LIST_STATE = {
  DYNAMIC: 'dynamic',           // No saved materials, calculating dynamically
  SAVED: 'saved',              // Materials saved and up-to-date
  OUTDATED: 'outdated'         // Materials saved but line items changed
};

/**
 * Get material list state based on saved materials and line items
 * @param {Object} savedMaterialList - Saved material list from database
 * @param {Array} currentLineItems - Current invoice line items
 * @returns {Object} State information
 */
export const getMaterialListState = (savedMaterialList, currentLineItems) => {
  if (!savedMaterialList) {
    return {
      state: MATERIAL_LIST_STATE.DYNAMIC,
      badge: 'Auto-Calculated',
      badgeColor: 'processing',
      description: 'Materials calculated from line items in real-time'
    };
  }

  const hasChanges = detectLineItemChanges(currentLineItems, savedMaterialList.lineItemsSnapshot);

  if (hasChanges) {
    return {
      state: MATERIAL_LIST_STATE.OUTDATED,
      badge: 'Saved (Outdated)',
      badgeColor: 'warning',
      description: 'Saved materials may be outdated due to line item changes'
    };
  }

  return {
    state: MATERIAL_LIST_STATE.SAVED,
    badge: 'Saved',
    badgeColor: 'success',
    description: 'Materials saved and up-to-date'
  };
};

/**
 * Validate if line items are suitable for material calculation
 * @param {Array} lineItems - Line items to validate
 * @returns {Object} Validation result
 */
export const validateLineItemsForMaterials = (lineItems) => {
  if (!lineItems || !Array.isArray(lineItems) || lineItems.length === 0) {
    return {
      isValid: false,
      message: 'No line items found'
    };
  }

  const validItems = lineItems.filter(item => 
    item.workingHeight && 
    item.width && 
    item.quantity > 0
  );

  // FIXED: Always return isValid: true if line items exist, so the material list section shows
  // The MaterialListManager component will handle displaying appropriate messages
  // about which items need working height and width
  return {
    isValid: true,
    validItemCount: validItems.length,
    totalItemCount: lineItems.length,
    hasValidItems: validItems.length > 0,
    message: validItems.length === 0 
      ? 'Please specify working height and width for line items to calculate materials'
      : validItems.length < lineItems.length
        ? `${validItems.length} of ${lineItems.length} items have material specifications`
        : 'All line items have material specifications'
  };
};
